import { useMemo } from "react"
import Highcharts from "highcharts/highstock"
import { DataframeData, Mask } from "../../PortfolioManagement/types"

export const useHighchartOptions = (
  apiData: DataframeData,
  highlightLastRow: boolean,
): Highcharts.Options => {
  const chartData = useMemo(() => {
    // Non-date metadata columns to exclude
    const metadataColumns = [
      "metric",
      "id",
      "query_key",
      "_idx",
      "transformation",
      "transformation_applied",
      "pop_data_type",
    ]

    // Get all date columns (excluding metadata columns)
    const dateColumns = apiData.columns
      .filter(col => !metadataColumns.includes(col.field))
      .map(col => col.field)

    // Get forecasted and backcasted masks
    const forecastedMasks = apiData.masks?.["forecasted"] || []
    const backcastedMasks = apiData.masks?.["backcasted"] || []

    // Highcharts default colors for consistency
    const defaultColors = Highcharts.getOptions().colors || []

    // Helper to parse values
    const parseValue = (value: any) =>
      value !== null && value !== undefined && value !== ""
        ? Number(Number(value).toFixed(2))
        : null

    // Calculate which dates are forecasted across all series (for average row)
    const allForecastedDates = new Set<string>()
    forecastedMasks.forEach((mask: Mask) => {
      const fields = (mask as any)?.fields || []
      if (Array.isArray(fields)) {
        fields.forEach((field: string) => allForecastedDates.add(field))
      }
    })

    // Transform data for each metric - creating separate series for historical and forecasted
    return apiData.rows.flatMap((row, rowIndex) => {
      const series: any[] = []

      // Check if this is an average row for special styling
      const metricName = String(row.metric || "").toLowerCase()
      const isAverageRow = metricName.includes("average") || metricName.includes("avg")

      // Get the row's actual position index (used for mask lookup)
      const rowPosition = row._idx as number

      // Find mask for this row (for average, use all forecasted dates)
      const forecastMask = forecastedMasks.find((m: Mask) => m._idx === rowPosition)
      const forecastedFields = isAverageRow
        ? Array.from(allForecastedDates)
        : (forecastMask as any)?.fields || []

      // Find backcasted fields for this row
      const backcastMask = backcastedMasks.find((m: Mask) => m._idx === rowPosition)
      const backcastedFields = (backcastMask as any)?.fields || []

      // Separate dates into historical and forecasted
      const historicalDates = dateColumns.filter(
        date => !Array.isArray(forecastedFields) || !forecastedFields.includes(date),
      )
      const forecastedDates = dateColumns.filter(
        date => Array.isArray(forecastedFields) && forecastedFields.includes(date),
      )

      // Get color for this metric (cycle through default colors, orange for average)
      // Priority: average > highlightLastRow > default
      const seriesColor = isAverageRow
        ? "#FFA500" // Orange for average
        : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
          ? "#8884d8"
          : defaultColors[rowIndex % defaultColors.length]

      // Special styling for average row (takes precedence over highlightLastRow)
      const lineWidth = isAverageRow
        ? 3
        : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
          ? 2
          : 1.5
      const zIndex = isAverageRow
        ? 10
        : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
          ? 5
          : undefined
      const markerSymbol = isAverageRow ? "diamond" : "circle"
      const markerRadius = isAverageRow
        ? 5
        : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
          ? 4
          : 3

      // Sort dates to find the transition point
      const sortedHistoricalDates = [...historicalDates].sort()
      const sortedForecastedDates = [...forecastedDates].sort()
      const lastHistoricalDate = sortedHistoricalDates[sortedHistoricalDates.length - 1]
      const lastHistoricalValue = lastHistoricalDate ? parseValue(row[lastHistoricalDate]) : null
      const firstForecastedDate = sortedForecastedDates[0]
      const firstForecastedValue = firstForecastedDate ? parseValue(row[firstForecastedDate]) : null

      // Historical series (solid line)
      if (historicalDates.length > 0) {
        const historicalData = historicalDates
          .map(date => {
            const isBackcasted = Array.isArray(backcastedFields) && backcastedFields.includes(date)
            return {
              x: new Date(date).getTime(),
              y: parseValue(row[date]),
              marker: isBackcasted ? ({ symbol: "triangle" } as any) : undefined,
            }
          })
          .sort((a, b) => a.x - b.x)

        // Add first forecasted point at the end for visual continuity
        if (forecastedDates.length > 0 && firstForecastedDate && firstForecastedValue !== null) {
          const isBackcasted =
            Array.isArray(backcastedFields) && backcastedFields.includes(firstForecastedDate)
          historicalData.push({
            x: new Date(firstForecastedDate).getTime(),
            y: firstForecastedValue,
            marker: isBackcasted ? ({ symbol: "triangle" } as any) : undefined,
          })
        }

        series.push({
          type: "line" as const,
          name: String(row.metric),
          id: `${rowIndex}-historical`,
          visible: true,
          showInLegend: true,
          data: historicalData,
          color: seriesColor,
          dashStyle: "Solid",
          lineWidth: lineWidth,
          zIndex: zIndex,
          marker: {
            enabled: true,
            radius: markerRadius,
            symbol: markerSymbol,
            ...(isAverageRow && {
              fillColor: seriesColor,
              lineColor: seriesColor,
              lineWidth: 2,
            }),
            ...(!isAverageRow &&
              highlightLastRow &&
              rowIndex === apiData.rows.length - 1 && {
                fillColor: "transparent",
                lineWidth: 2,
                lineColor: seriesColor,
              }),
          },
        })
      }

      // Forecasted series (dotted line) - includes last historical point for continuity
      if (forecastedDates.length > 0) {
        const forecastedData = forecastedDates
          .map(date => {
            const isBackcasted = Array.isArray(backcastedFields) && backcastedFields.includes(date)
            return {
              x: new Date(date).getTime(),
              y: parseValue(row[date]),
              marker: isBackcasted ? ({ symbol: "triangle" } as any) : undefined,
            }
          })
          .sort((a, b) => a.x - b.x)

        // Add last historical point at the beginning for visual continuity
        if (lastHistoricalDate && lastHistoricalValue !== null) {
          const isBackcasted =
            Array.isArray(backcastedFields) && backcastedFields.includes(lastHistoricalDate)
          forecastedData.unshift({
            x: new Date(lastHistoricalDate).getTime(),
            y: lastHistoricalValue,
            marker: isBackcasted ? ({ symbol: "triangle" } as any) : undefined,
          })
        }

        series.push({
          type: "line" as const,
          name: `${row.metric} (Forecasted)`,
          id: `${rowIndex}-forecasted`,
          visible: true,
          showInLegend: true,
          linkedTo: `${rowIndex}-historical`,
          data: forecastedData,
          color: seriesColor,
          dashStyle: "Dot",
          lineWidth: lineWidth,
          zIndex: zIndex,
          marker: {
            enabled: true,
            radius: isAverageRow
              ? 6
              : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
                ? 5
                : 4,
            symbol: markerSymbol,
            ...(isAverageRow && {
              fillColor: seriesColor,
              lineColor: seriesColor,
              lineWidth: 2,
            }),
            ...(!isAverageRow &&
              highlightLastRow &&
              rowIndex === apiData.rows.length - 1 && {
                fillColor: "transparent",
                lineWidth: 2,
                lineColor: seriesColor,
              }),
          },
        })
      }

      // If no series were created (no split between historical and forecasted), create a single series
      if (series.length === 0) {
        series.push({
          type: "line" as const,
          name: String(row.metric),
          id: `${rowIndex}-all`,
          visible: true,
          showInLegend: true,
          color: seriesColor,
          data: dateColumns
            .map(date => {
              const isBackcasted =
                Array.isArray(backcastedFields) && backcastedFields.includes(date)
              return {
                x: new Date(date).getTime(),
                y: parseValue(row[date]),
                marker: isBackcasted ? ({ symbol: "triangle" } as any) : undefined,
              }
            })
            .sort((a, b) => a.x - b.x),
          lineWidth: isAverageRow
            ? 3
            : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
              ? 1.5
              : 1,
          zIndex: zIndex,
          marker: {
            enabled: true,
            radius: isAverageRow
              ? 5
              : !isAverageRow && highlightLastRow && rowIndex === apiData.rows.length - 1
                ? 4
                : 2,
            symbol: markerSymbol,
            ...(isAverageRow && {
              fillColor: seriesColor,
              lineColor: seriesColor,
              lineWidth: 2,
            }),
            ...(!isAverageRow &&
              highlightLastRow &&
              rowIndex === apiData.rows.length - 1 && {
                fillColor: "transparent",
                lineWidth: 2,
                lineColor: seriesColor,
              }),
          },
        })
      }

      return series
    })
  }, [apiData, highlightLastRow])

  // Generate custom buttons only if highlightLastRow is true
  const customButtons = useMemo(() => {
    const buttons: { [key: string]: Highcharts.ExportingButtonsOptionsObject } = {}

    if (highlightLastRow) {
      // Get the latest row's dataset name
      const latestRowIndex = apiData.rows.length - 1
      const latestRowName = apiData.rows[latestRowIndex]?.metric || "Latest"

      // "Show All" button
      buttons.showAll = {
        text: "Show All",
        onclick: function (this: Highcharts.Chart) {
          this.series.forEach((series: any) => series.setVisible(true, false))
          this.redraw()
        },
      }

      // "Latest Only" button with dynamic name
      buttons.latestOnly = {
        text: `${latestRowName} Only`,
        onclick: function (this: Highcharts.Chart) {
          // Show only series for the last row (both historical and forecasted)
          const lastIndex = apiData.rows.length - 1
          this.series.forEach((series: any) => {
            // Check if series id starts with the last row index
            const seriesId = series.options.id || ""
            const isLastRow =
              seriesId === `${lastIndex}-historical` ||
              seriesId === `${lastIndex}-forecasted` ||
              seriesId === `${lastIndex}-all`

            series.setVisible(isLastRow, false)
          })
          this.redraw()
        },
      }
    }

    return buttons
  }, [apiData, highlightLastRow])

  return {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      style: {
        fontFamily: "inherit",
      },
      zoomType: undefined,
      pinchType: undefined,
    },
    title: {
      text: undefined, // No main title
    },
    subtitle: {
      text: "Click on legend items to hide/show data series",
      style: {
        color: "#ccc",
        fontSize: "12px",
      },
    },
    exporting: {
      enabled: true,
      buttons: customButtons,
    },
    rangeSelector: {
      enabled: true,
      inputEnabled: false,
      selected: 2, // Select 5Y button by default
      buttons: [
        {
          type: "month",
          count: 6,
          text: "6M",
        },
        {
          type: "year",
          count: 1,
          text: "1Y",
        },
        {
          type: "year",
          count: 5,
          text: "5Y",
        },
        {
          type: "all",
          text: "All",
        },
      ],
    },
    navigator: {
      enabled: true,
      height: 50,
      margin: 25,
      xAxis: {
        labels: {
          style: {
            color: "#fff",
          },
        },
      },
    },
    scrollbar: {
      enabled: true,
      barBackgroundColor: "rgba(100, 100, 100, 0.6)",
      barBorderRadius: 5,
      barBorderWidth: 0,
      buttonBackgroundColor: "rgba(100, 100, 100, 0.6)",
      buttonBorderWidth: 0,
      buttonBorderRadius: 5,
      trackBackgroundColor: "rgba(50, 50, 50, 0.6)",
      trackBorderWidth: 0,
      trackBorderRadius: 5,
      rifleColor: "#fff",
      buttonArrowColor: "#fff",
    },
    xAxis: {
      type: "datetime",
      labels: {
        format: "{value:%Y-%m-%d}",
        style: {
          color: "#fff",
        },
      },
      gridLineColor: "#444",
      lineColor: "#444",
      tickColor: "#444",
      ordinal: false,
      // Find the maximum date in the data (includes predictions)
      min: (() => {
        const maxDate = Math.max(
          ...apiData.columns
            .filter(col => !["metric", "id"].includes(col.field))
            .map(col => new Date(col.field).getTime())
            .filter(time => !isNaN(time)),
        )
        return maxDate - 5 * 365 * 24 * 60 * 60 * 1000 // 5 years before max date
      })(),
      max: (() => {
        const maxDate = Math.max(
          ...apiData.columns
            .filter(col => !["metric", "id"].includes(col.field))
            .map(col => new Date(col.field).getTime())
            .filter(time => !isNaN(time)),
        )
        return maxDate // Latest date in data (includes predictions)
      })(),
    },
    yAxis: {
      title: {
        text: "Value",
        style: {
          color: "#fff",
        },
      },
      labels: {
        style: {
          color: "#fff",
        },
      },
      gridLineColor: "#444",
    },
    legend: {
      enabled: true,
      itemStyle: {
        color: "#fff",
        cursor: "pointer",
        fontWeight: "normal",
      },
      itemHoverStyle: {
        color: "#ccc",
      },
      itemHiddenStyle: {
        color: "#666",
      },
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemMarginTop: 5,
      itemMarginBottom: 5,
      maxHeight: 100,
      navigation: {
        activeColor: "#fff",
        inactiveColor: "#666",
        animation: true,
        arrowSize: 12,
        style: {
          fontWeight: "bold",
          color: "#fff",
        },
      },
      itemCheckboxStyle: {
        width: 13,
        height: 13,
        position: "absolute",
        top: "-2px",
        left: "-2px",
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: "%Y-%m-%d",
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      style: {
        color: "#fff",
      },
    },
    plotOptions: {
      series: {
        marker: {
          enabled: true,
          radius: 2,
        },
        lineWidth: 1,
        showInLegend: true,
        connectNulls: true,
        events: {
          legendItemClick: function () {
            // Return false to prevent default behavior if you want custom handling
            // For now, we'll use the default Highcharts behavior
            return true
          },
        },
      },
    },
    series: chartData,
  }
}
